import React, { useState, useRef, useEffect } from 'react';
import { Box, Typography, TextField, Button, IconButton, Tooltip } from '@mui/material';
import { Add as AddIcon, Close as CloseIcon, Undo as UndoIcon } from '@mui/icons-material';
import { OptionAlreadyExistsInfoIcon } from './OptionAlreadyExistsInfoIcon';

export { OptionAlreadyExistsInfoIcon };


export interface ChoiceOptionsSectionProps {
  options: string[];
  initialCount?: number; // Options with index < initialCount cannot be deleted
  initialOptions?: string[]; // Used to detect renames and enable Undo
  addInput: string;
  onAddInputChange: (val: string) => void;
  onAddOption: () => void;
  addError: string | null;
  onClearAddError?: () => void;
  onOptionRename: (index: number, newLabel: string) => void;
  onUndoOptionRename?: (index: number) => void;
  onRemoveOption: (index: number) => void;
  optionErrors?: Record<number, string>;
}

export const ChoiceOptionsSection: React.FC<ChoiceOptionsSectionProps> = React.memo(({
  options,
  initialCount = 0,
  initialOptions = [],
  addInput,
  onAddInputChange,
  onAddOption,
  addError,
  onClearAddError,
  onOptionRename,
  onUndoOptionRename,
  onRemoveOption,
  optionErrors = {},
}) => {
  const [activeWarningIndex, setActiveWarningIndex] = useState<number | null>(null);
  const [addWarning, setAddWarning] = useState<boolean>(false);
  const addInputRef = useRef<HTMLInputElement>(null);
  const optionsContainerRef = useRef<HTMLDivElement>(null);
  const prevCountRef = useRef<number>(options.length);

  const scrollToBottom = () => {
    requestAnimationFrame(() => {
      setTimeout(() => {
        if (optionsContainerRef.current) {
          const container = optionsContainerRef.current;
          container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
        }
      }, 60);
    });
  };

  useEffect(() => {
    if (options.length > prevCountRef.current) {
      scrollToBottom();
    }
    prevCountRef.current = options.length;
  }, [options.length]);

  const handleAddClick = () => {
    onAddOption();
    setAddWarning(false);
    addInputRef.current?.focus();
    requestAnimationFrame(() => {
      addInputRef.current?.focus();
    });
  };

  const handleUndo = (index: number) => {
    if (onUndoOptionRename) {
      onUndoOptionRename(index);
    } else if (initialOptions[index] !== undefined) {
      onOptionRename(index, initialOptions[index]);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, width: '100%' }}>
      {/* Add New Option Section */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Add New Option
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <TextField
              inputRef={addInputRef}
              fullWidth
              size="small"
              placeholder="Type new option"
              value={addInput}
              inputProps={{ maxLength: 25 }}
              onChange={(e) => {
                const val = e.target.value;
                onAddInputChange(val);
                setAddWarning(val.length >= 25);
                if (addError && onClearAddError) {
                  onClearAddError();
                }
              }}
              onFocus={(e) => {
                setAddWarning(e.target.value.length >= 25);
              }}
              onBlur={() => {
                setAddWarning(false);
                if (addError && onClearAddError && !addInput.trim()) {
                  onClearAddError();
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  e.stopPropagation();
                  handleAddClick();
                }
              }}
              error={Boolean(addError)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  height: 38,
                  fontSize: '0.875rem',
                  bgcolor: '#FFFFFF',
                  borderColor: addWarning ? 'warning.main' : undefined,
                },
              }}
            />
            {addWarning && !addError && (
              <Typography variant="caption" sx={{ color: 'warning.main', mt: 0.5, fontSize: '0.75rem', fontWeight: 500 }}>
                Option cannot exceed 25 characters
              </Typography>
            )}
            {addError && (
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                <Typography variant="caption" sx={{ color: 'error.main', fontSize: '0.75rem', fontWeight: 500 }}>
                  {addError}
                </Typography>
                {addError.toLowerCase().includes('already exists') && <OptionAlreadyExistsInfoIcon />}
              </Box>
            )}
          </Box>

          <Button
            variant="contained"
            color="primary"
            onClick={handleAddClick}
            startIcon={<AddIcon fontSize="small" />}
            sx={{
              height: 38,
              px: 2,
              flexShrink: 0,
              borderRadius: '8px',
              boxShadow: 'none',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.85rem',
            }}
          >
            Add
          </Button>
        </Box>
      </Box>

      {/* Options List Outer Box Container */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Options List ({options.length})
        </Typography>

        <Box
          sx={{
            p: 1,
            border: '1px solid #E2E8F0',
            borderRadius: '10px',
            bgcolor: '#FFFFFF',
          }}
        >
          {options.length > 0 ? (
            <Box
              ref={optionsContainerRef}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.6,
                maxHeight: { xs: '50vh', sm: 260 },
                overflowY: 'auto',
                WebkitOverflowScrolling: 'touch',
                pr: 0.5,
              }}
            >
              {options.map((optVal, index) => {
                const err = optionErrors[index];
                const isNewlyAdded = index >= initialCount;
                const origVal = initialOptions[index];
                const isModified = index < initialCount && origVal !== undefined && optVal !== origVal;
                const isWarningThis = activeWarningIndex === index;

                return (
                  <Box
                    key={`option-row-${index}`}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.25,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        py: 0.25,
                        px: 0.5,
                        borderRadius: '8px',
                        bgcolor: 'transparent',
                        transition: 'background-color 0.15s ease',
                        '&:hover': {
                          bgcolor: '#F8FAFC',
                        },
                      }}
                    >
                      <TextField
                        fullWidth
                        size="small"
                        value={optVal}
                        inputProps={{ maxLength: 25 }}
                        onChange={(e) => {
                          const val = e.target.value;
                          onOptionRename(index, val);
                          if (val.length >= 25) {
                            setActiveWarningIndex(index);
                          } else {
                            setActiveWarningIndex(null);
                          }
                        }}
                        onFocus={(e) => {
                          if (e.target.value.length >= 25) {
                            setActiveWarningIndex(index);
                          }
                        }}
                        onBlur={() => {
                          setActiveWarningIndex(null);
                        }}
                        error={Boolean(err)}
                        variant="outlined"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '6px',
                            height: 36,
                            fontSize: '0.875rem',
                            bgcolor: isModified ? '#F8FAFC' : isNewlyAdded ? '#F5F3FF' : '#FFFFFF',
                            borderColor: err
                              ? 'error.main'
                              : isModified
                                ? '#3B82F6'
                                : isNewlyAdded
                                  ? '#6366F1'
                                  : isWarningThis
                                    ? 'warning.main'
                                    : '#CBD5E1',
                            transition: 'all 0.15s ease',
                            '&:hover': {
                              borderColor: err
                                ? 'error.main'
                                : isModified
                                  ? '#2563EB'
                                  : isNewlyAdded
                                    ? '#4F46E5'
                                    : '#94A3B8',
                            },
                          },
                        }}
                      />

                      {/* Undo Icon Button using standard Material UI Undo curved arrow icon */}
                      {isModified && (
                        <Tooltip title="Undo change" arrow placement="bottom">
                          <IconButton
                            size="small"
                            onClick={() => handleUndo(index)}
                            sx={{
                              width: 32,
                              height: 32,
                              color: '#2563EB',
                              border: '1px solid #BFDBFE',
                              bgcolor: '#F0F7FF',
                              borderRadius: '6px',
                              transition: 'all 0.15s ease',
                              '&:hover': {
                                color: '#1D4ED8',
                                bgcolor: '#DBEAFE',
                                borderColor: '#93C5FD',
                              },
                              flexShrink: 0,
                            }}
                          >
                            <UndoIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}

                      {/* Cross Icon Button for newly added options */}
                      {isNewlyAdded && (
                        <Tooltip title="Remove option" arrow placement="bottom">
                          <IconButton
                            size="small"
                            onClick={() => onRemoveOption(index)}
                            sx={{
                              color: '#4F46E5',
                              width: 32,
                              height: 32,
                              p: 0.75,
                              border: '1px solid #C7D2FE',
                              bgcolor: '#EEF2FF',
                              borderRadius: '6px',
                              transition: 'all 0.15s ease',
                              '&:hover': {
                                color: 'error.main',
                                bgcolor: '#FEF2F2',
                                borderColor: '#FCA5A5',
                              },
                              flexShrink: 0,
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>

                    {isWarningThis && !err && (
                      <Typography variant="caption" sx={{ color: 'warning.main', px: 1, fontSize: '0.75rem', fontWeight: 500 }}>
                        Option cannot exceed 25 characters
                      </Typography>
                    )}

                    {err && (
                      <Box sx={{ display: 'flex', alignItems: 'center', px: 1 }}>
                        <Typography variant="caption" sx={{ color: 'error.main', fontSize: '0.75rem', fontWeight: 500 }}>
                          {err}
                        </Typography>
                        {err.toLowerCase().includes('already exists') && <OptionAlreadyExistsInfoIcon />}
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          ) : (
            <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic', py: 1 }}>
              No choice options added yet. Type an option name above and click + Add.
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
});

ChoiceOptionsSection.displayName = 'ChoiceOptionsSection';
